import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ActivatedRoute } from '@angular/router';
import { map, startWith } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { AsyncPipe, NgIf } from '@angular/common';
import { LanguageTypes } from '@shared/enums';
import { CurrencyTypes, LocationFormCardComponent } from '@ep/shared';
import { Location } from '@shared/models';
import { LocationFormComponent } from '@app/areas/merchants/forms/location-form/location-form.component';
import { LocationFormService } from '@app/areas/merchants/services/location-form.service';
import { LocationsApiService } from '@app/areas/merchants/services/locations.api.service';

@Component({
  selector: 'app-location-detail',
  templateUrl: './location-detail.component.html',
  styleUrls: ['./location-detail.component.scss'],
  standalone: true,
  imports: [LocationFormComponent, LocationFormCardComponent, AsyncPipe, NgIf],
})
export class LocationDetailComponent implements OnInit, OnDestroy {
  private readonly locationFormService = inject(LocationFormService);
  private readonly locationsApiService = inject(LocationsApiService);
  private readonly messageService = inject(MessageService);
  private readonly route = inject(ActivatedRoute);

  protected location: Location | null = null;
  protected notFound = false;
  protected isSaving = false;

  protected mode$ = this.locationFormService.mode$;
  protected isSaveDisabled$ = this.locationFormService.getForm().statusChanges.pipe(
    startWith(this.locationFormService.getForm().status),
    map((status) => status !== 'VALID')
  );

  private subs = new Subscription();

  ngOnInit(): void {
    this.subs.add(
      this.route.paramMap
        .pipe(map((params) => Number(params.get('locationId'))))
        .subscribe((locationId) => this.loadLocation(locationId))
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private loadLocation(locationId: number): void {
    this.subs.add(
      this.locationsApiService.getById(locationId).subscribe((location) => {
        if (!location) {
          this.notFound = true;
          return;
        }

        this.notFound = false;
        this.location = location;
        this.locationFormService.initializeForView(location);
      })
    );
  }

  protected edit(): void {
    if (this.location) {
      this.locationFormService.initializeForEdit(this.location);
    }
  }

  protected cancelEdit(): void {
    if (this.location) {
      this.locationFormService.initializeForView(this.location);
    }
  }

  protected saveLocation(): void {
    const form = this.locationFormService.getForm();
    if (!form.valid || !this.location) {
      return;
    }

    const merchantId = Number(this.route.parent?.parent?.snapshot.paramMap.get('merchantId'));
    this.isSaving = true;
    const { Details: formValue, Coordinates: coordinates } = form.getRawValue();

    this.subs.add(
      this.locationsApiService
        .update({
          LocationId: this.location.LocationId,
          MerchantId: merchantId,
          Name: formValue.Name!,
          Address: formValue.Address!,
          PhoneNumber: Number(formValue.PhoneNumber?.replace(/\D/g, '')),
          TimeZoneType: formValue.TimeZoneType!,
          CurrencyType: CurrencyTypes.Usd,
          LanguageType: LanguageTypes.English,
          Latitude: coordinates.Latitude!,
          Longitude: coordinates.Longitude!,
        })
        .subscribe((location) => {
          this.isSaving = false;
          if (location) {
            this.location = location;
            this.locationFormService.initializeForView(location);
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'ERROR: Update Location',
              detail: 'Something went wrong while updating the location',
            });
          }
        })
    );
  }
}
